import React, { useEffect, useState } from 'react';
import gql from 'graphql-tag';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import { People } from '@mui/icons-material';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';
import ListIcon from '@mui/icons-material/List';
import { DropzoneDialog } from 'mui-file-dropzone';
import GetAppIcon from '@mui/icons-material/GetApp';
import { AssignmentInd } from '@mui/icons-material';
import PublishIcon from '@mui/icons-material/Publish';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import {
  Checkbox,
  Autocomplete,
  Button,
  Stack,
  Switch,
  Typography,
  Alert,
  Tooltip,
  Fab,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  IconButton,
  Grid,
  FormControl,
  Avatar,
  LinearProgress,
  FormControlLabel,
} from '@mui/material';
import documentIcon from 'assets/img/icons/document.png';
import _ from 'lodash';
import { asyncForEach, isEmailValid, vendorContactRoleHumanReadable } from '../utils';
import { logError } from 'utils/logger';
import Tile from './Tile/Tile';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { documentsByKey, referenceTypes } from '../pages/Prequal/constants';
import {
  assigneeKinds,
  prioritySelectOptions,
  statusSelectOptions,
  taskItemTypes,
  taskStatuses,
} from '../constants/tasks';
import { rejectHiddenDocumentTypeOptions } from '../services/pcwService';
import {
  collectCreateTaskInput,
  collectUpdateTaskInput,
  collectUploadDocumentInput,
  getAssigneeKind,
  getAvailableSpecialTaskItemTypes,
  getRegardingForAssigneeKind,
  getSpecialTaskItemType,
  getSpecialTaskItemTypeConfig,
  isReferenceAssigneeKind,
  resolveCompletedDateTime,
} from './TasksCommon/lib';
import { TradeReferenceResults } from './TasksCommon/components/TradeReferenceResults';
const config = require('../config');

const useStyles = makeStyles(theme => ({
  surface: {
    position: 'absolute',
    width: '80%',
    height: 839,
    display: 'flex',
    '@media (max-width: 660px)': {
      width: '90%',
      minWidth: '0',
    },
  },
  formContainer: {
    height: 'inherit',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',

    '@media (max-width: 660px)': {
      width: '100%',
      minWidth: '0',
      margin: '0 auto',
    },
  },
  formContainerGridContainer: {
    width: '100%',
    margin: 0,
    '& .MuiGrid-grid-xs-12': {
      paddingTop: 0,
      paddingBottom: 0,
    },
    '@media (max-width: 599px)': {
      width: '100%',
      margin: 0,
      '& .MuiGrid-grid-xs-12': {
        padding: 0,
      },
    },
  },
  closeButton: {
    position: 'absolute',
    right: '20px',
    top: theme.spacing(1),
    color: theme.palette.grey[500],
    zIndex: '19',
    cursor: 'pointer',
    width: '30px',
    height: '30px',
  },
  previewDoc: {
    maxWidth: '60%',
  },
  subHeaderContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  taskTitle: {
    fontSize: '20px',
    fontWeight: '500',
  },
  taskAssigned: {
    margin: '0 0 1rem 0',
    fontSize: '20px',
    fontWeight: '300',
  },
  titleChatText: {
    fontSize: '16px',
    fontWeight: '500',
  },
  notifyCheckboxesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chatCheckboxStyles: {
    color: '#0176FF !important',
    padding: '0px 5px 2px 9px',
  },
  textField: {
    margin: '8px 0',
    width: '100%',
    height: 40,

    '& input': {
      padding: 12,
      fontSize: 14,
    },
    '& fieldset:disabled': {
      borderColor: '#f44336',
    },
    '& .MuiOutlinedInput-input': {
      padding: '12px 24px',
    },
    '& fieldset legend span': {
      fontSize: 12,
      fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
      textTransform: 'none',
      fontWeight: 'normal',
    },
    "& .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] .MuiAutocomplete-input": {
      padding: '4px 18px',
    },
  },
}));

const getModalStyle = () => {
  return {
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };
};

const UPLOAD_FILE = gql`
  mutation ($input: UpsertDocumentInput!, $file: Upload) {
    upsertDocument(input: $input, file: $file)
  }
`;

const GET_LENDER_PROFILE_CONTACTS = gql`
  query GetLenderProfileByDynamicsId($lenderProfileDynamicsId: ID!) {
    getLenderProfileByDynamicsId(lenderProfileDynamicsId: $lenderProfileDynamicsId) {
      lenderMatrixMatchingRules
      lenderProfileUsers {
        accessDealerLocationIds
        accessDealerUserIds
        lenderProfileId
        lenderProfileToUserId
        lenderUserProfileId
        receivesNotifications
        notificationContact
        userProfile {
          # account
          id
          accountId
          adminRole
          availability
          dynamicsContactId
          email
          firstName
          lastName
          fullName
          title
          status
          lastLogin
          phoneNumber
          mobilePhoneNumber
        }
      }
    }
  }
`;

const GET_CREDIT_SUBMISSIONS = gql`
  query CreditSubmissionsByDynamicsVOId($id: ID!, $display: String) {
    creditSubmissionsByDynamicsVOId(id: $id, display: $display) {
      creditSubmissionId
      creditSubId
      submissionDate
      decision
      lenderName
      amountRequested
      approvedAmount
      notes
      contingencies
      approvedRate
      creditSubStage
      apiApplicationId
      apiResult
      apiExpiredCredentials
      apiDetail
      lenderProfileId
      lenderChannelCode
      lenderTypeCode
      draft
      creditSubStage
      transactionSummary
      mappedValues
      source
      submittedBy
      lenderContactInfo {
        id
        email
        dynamicsContactId
        fullName
        title
        phoneNumber
        accountId
      }
    }
  }
`;

const GET_VENDOR_OPPORTUNITY = gql`
  query VendorOpportunityById($id: ID!) {
    vendorOpportunity(id: $id) {
      vendorOpportunityId
      dynamicsVendorOpportunityId
    }
  }
`;

const GET_CREDIT_APP_BY_VO = gql`
  query CreditApplicationByVO($VOId: ID!) {
    creditApplication(VOId: $VOId) {
      id
      dynamicsId
      primaryContact {
        ownerPgId
        firstName
        lastName
        email
      }
      creditApplicationOwner {
        ownerPgId
        firstName
        lastName
        email
      }
      references {
        referenceId
        referenceType
        contactEmail
        contactName
        companyName
      }
    }
  }
`;

const DOCUMENT_E_SIGN_DYNAMICS_ACCOUNT_IDS = [
  '52f87212-4873-ee11-8171-066ef92796c9', // Test Vendor Auto
  '55b8872c-19b6-ee11-8171-066ef92796c9', // Test Vendor v4
  'bb71f539-02f0-eb11-813b-066ef92796c9', // Burris Equipment
  'de89d6a4-173e-ea11-8115-005056b05a0f', // TRNSACT Tractors
];

const MAX_UPLOAD_FILE_SIZE = 25000000;
const DEFAULT_DUE_DATE_OFFSET_DAYS = 7;

export default function DealerTaskForm(props) {
  const classes = useStyles();

  const {
    handleAddTask,
    handleSaveTask,
    handleClose,
    refetchDocs,
    creditSubId,
    taskId, //NULL FOR NEW TASKS
    tasksData,
    relatedUsers,
    lenderProfileDynamicsId,
    documentsPortalConfiguration,
    documents,
    ocaTemplate,
  } = props;

  const account = useSelector(state => state.account);
  const userProfile = useSelector(state => state.userProfile);

  const lenderProfiles = useSelector(state => state.lp);
  const creditApp = useSelector(state => state.creditApp);
  const vo = useSelector(state => state.vo);

  const [modalStyle] = useState(getModalStyle);
  const [task, setTask] = useState();
  const [enabledForSMS, setEnabledForSMS] = useState(null);
  const [assignedByAccount, setAssignedByAccount] = useState('');
  const [assignedByUser, setAssignedByUser] = useState('');
  const [assignedByUserProfileId, setAssignedByUserProfileId] = useState('');
  const [assignee, setAssignee] = useState('');
  const [priority, setPriority] = useState('medium');
  const [regarding, setRegarding] = useState('');
  const [status, setStatus] = useState(taskStatuses.open);
  const [isSystemTask, setIsSystemTask] = useState(false);
  const [assigneeList, setAssigneeList] = useState([]);
  const [vendorOpportunityId, setVendorOpportunityId] = useState(null);
  const [notifyByEmail, setNotifyByEmail] = useState(true);
  const [notifyByText, setNotifyByText] = useState(false);
  const [avatar, setAvatar] = useState('');
  const [taskItems, setTaskItems] = useState([]);
  const [assignedToUserProfileId, setAssignedToUserProfileId] = useState(null);
  const [assignedToOwnerPgId, setAssignedToOwnerPgId] = useState(null);
  const [openUploadZone, setOpenUploadZone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [taskAssignedToMe, setTaskAssignedToMe] = useState(false);
  const [creditSubmission, setCreditSubmission] = useState();
  const [completedDateTime, setCompletedDateTime] = useState(null);
  const [taskItemIndex, setTaskItemIndex] = useState(0);
  const [taskDueDateTime, setTaskDueDateTime] = useState(moment().add(DEFAULT_DUE_DATE_OFFSET_DAYS, 'd').toISOString());
  const [taskNotificationContactIds, setTaskNotificationContactIds] = useState([]);
  const [notifyAssignorOnReminder, setNotifyAssignorOnReminder] = useState(true);

  const { data: lpContactsData } = useQuery(GET_LENDER_PROFILE_CONTACTS, {
    variables: {
      lenderProfileDynamicsId: lenderProfileDynamicsId || '',
      fetchPolicy: 'no-cache',
    },
  });

  // Fetch VO data to recover dynamicsVendorOpportunityId as a fallback.
  // On VO details, this is already in Redux state and will be skipped.
  // On consolidated tasks, we need this lookup to hydrate the Dynamics VO id
  // for downstream credit submissions query.
  const shouldSkipVendorOpportunityQuery = !!vo?.dynamicsVendorOpportunityId || !task?.vendorOpportunityId;
  const { data: taskVendorOpportunityData, loading: taskVendorOpportunityLoading } = useQuery(GET_VENDOR_OPPORTUNITY, {
    variables: {
      id: task?.vendorOpportunityId,
    },
    skip: shouldSkipVendorOpportunityQuery,
  });

  // Fetch credit app here only as a fallback for contexts where Redux does not
  // already contain a populated creditApp object, such as consolidated tasks.
  const shouldSkipCreditAppByVoQuery = !task?.vendorOpportunityId || !!creditApp?.id;
  const { data: creditAppByVoData, loading: creditAppByVoLoading } = useQuery(GET_CREDIT_APP_BY_VO, {
    variables: {
      VOId: task?.vendorOpportunityId,
    },
    skip: shouldSkipCreditAppByVoQuery,
  });

  const effectiveCreditApp = creditAppByVoData?.creditApplication || creditApp;

  const dynamicsVendorOpportunityId =
    vo?.dynamicsVendorOpportunityId || taskVendorOpportunityData?.vendorOpportunity?.dynamicsVendorOpportunityId;

  const { data: creditSubmissionsData } = useQuery(GET_CREDIT_SUBMISSIONS, {
    variables: {
      id: dynamicsVendorOpportunityId,
    },
    skip: !dynamicsVendorOpportunityId,
  });

  const [fileInput] = useMutation(UPLOAD_FILE, {
    context: { authRequired: true },
  });

  const documentTypeOptionsForDealerAssignment = [
    documentsByKey.quote,
    documentsByKey.invoice,
    documentsByKey.buyers_order,
    documentsByKey.insurance_certificate,
    documentsByKey.driver_license,
    documentsByKey.bank_statements,
    documentsByKey.tax_returns,
    documentsByKey.other,
  ];
  const documentTypeOptionsForLenderAssignment = [
    documentsByKey.quote,
    documentsByKey.invoice,
    documentsByKey.buyers_order,
    documentsByKey.decision_letter,
    documentsByKey.insurance_certificate,
    documentsByKey.driver_license,
    documentsByKey.bank_statements,
    documentsByKey.tax_returns,
    documentsByKey.other,
  ];

  const documentTypeOptionsForApplicants = [
    documentsByKey.driver_license,
    documentsByKey.bank_statements,
    documentsByKey.tax_returns,
    documentsByKey.other,
  ];

  const isDocumentESignTaskAvailable = _.includes(DOCUMENT_E_SIGN_DYNAMICS_ACCOUNT_IDS, account.dynamicsAccountId);
  const documentsForESign = documents?.filter(d => d.docType === documentsByKey.quote.value);

  const availableSpecialTaskItemTypes = getAvailableSpecialTaskItemTypes(ocaTemplate);
  const specialTaskItemType = getSpecialTaskItemType(taskItems);

  const assigneeKind = getAssigneeKind(assignee, task);
  const assigneeIsApplicant = assigneeKind === assigneeKinds.applicant;
  const assigneeIsTradeReference = assigneeKind === assigneeKinds.tradeReference;
  const assigneeIsInsuranceReference = assigneeKind === assigneeKinds.insuranceReference;

  const defaultRegarding = _.get(vo, 'potentialCustomer.name', '');
  const isAssigneeEmailInvalid = isReferenceAssigneeKind(assigneeKind) && !isEmailValid(assignee?.email);

  const isSubmitEnabled = (() => {
    if (assigneeIsTradeReference) return !isAssigneeEmailInvalid;
    if (assigneeIsInsuranceReference) return !isAssigneeEmailInvalid && !!creditSubmission;
    return !_.isEmpty(assignee) && !_.isEmpty(taskItems);
  })();

  useEffect(() => {
    if (defaultRegarding) {
      setRegarding(defaultRegarding);
    }
    if (_.get(vo, 'vendorOpportunityId', false)) {
      setVendorOpportunityId(vo.vendorOpportunityId);
    }
    let availableLenderUserProfiles = [];
    let availableLenderAccounts = [];
    let accounts = [account];
    if (creditSubId) {
      if (lpContactsData?.getLenderProfileByDynamicsId?.lenderProfileUsers?.length > 0) {
        availableLenderUserProfiles = lpContactsData.getLenderProfileByDynamicsId.lenderProfileUsers.map(
          lpu => lpu.userProfile
        );

        availableLenderAccounts = [_.find(lenderProfiles, { lenderProfileId: lenderProfileDynamicsId })?.lenderAccount];
      } else {
        availableLenderUserProfiles = _.filter(lenderProfiles, profile => {
          if (profile.lenderUserProfile) {
            return profile;
          }
        }).map(item => item.lenderUserProfile);

        availableLenderAccounts = _.filter(lenderProfiles, profile => {
          if (profile.lenderAccount) {
            return profile;
          }
        }).map(item => item.lenderAccount);
      }
      accounts = [account, ...availableLenderAccounts];
    }

    let allRecipients = _.uniqBy([...relatedUsers, ...availableLenderUserProfiles], 'id')?.filter(
      user => user.status !== 'blocked'
    );

    let assigneeListToSet = _.map(effectiveCreditApp?.creditApplicationOwner || [], owner => {
      const isPrimaryContact = owner.ownerPgId === effectiveCreditApp?.primaryContact?.ownerPgId;
      return {
        ownerPgId: owner.ownerPgId,
        firstName: owner.firstName,
        fullName: `${owner.firstName} ${owner.lastName}`,
        lastName: owner.lastName,
        email: owner.email,
        company: 'Customer',
        parenthesesText: isPrimaryContact ? 'Primary Contact' : 'Contact',
        isApplicant: true,
      };
    });
    const primaryContact = effectiveCreditApp?.primaryContact;
    if (primaryContact && !_.find(assigneeListToSet, { email: primaryContact.email })) {
      assigneeListToSet.push({
        ownerPgId: primaryContact.ownerPgId,
        firstName: primaryContact.firstName,
        fullName: `${primaryContact.firstName} ${primaryContact.lastName}`,
        lastName: primaryContact.lastName,
        email: primaryContact.email,
        company: 'Customer',
        parenthesesText: 'Primary Contact',
        isApplicant: true,
      });
    }
    assigneeListToSet.push(
      ...allRecipients.map(up => {
        const account = _.find(accounts, {
          id: up.accountId,
        });
        if (account) {
          if (up.vendorContactRole) {
            return {
              ...up,
              company: 'Internal',
              parenthesesText: vendorContactRoleHumanReadable[up.vendorContactRole],
            };
          } else {
            return {
              ...up,
              company: account.name,
              parenthesesText: 'Lender',
            };
          }
        }
      })
    );

    const tradeReferences = effectiveCreditApp?.references?.filter(r => r.referenceType === referenceTypes.trade);
    if (tradeReferences?.length) {
      const tradeReferenceAsassignees = tradeReferences.map(reference => ({
        referenceId: reference.referenceId,
        company: 'Trade Reference',
        email: reference.contactEmail,
        fullName: `${reference.companyName}`,
        parenthesesText: `${reference.contactName}${reference.contactEmail ? ` - ${reference.contactEmail}` : ``}`,
        isTradeReference: true,
      }));
      assigneeListToSet = [...tradeReferenceAsassignees, ...assigneeListToSet];
    }

    const insuranceReference = effectiveCreditApp?.references?.find(r => r.referenceType === referenceTypes.insurance);
    const creditSubmissions = creditSubmissionsData?.creditSubmissionsByDynamicsVOId;
    if (insuranceReference && creditSubmissions?.length) {
      const insuranceReferenceAsassignee = {
        referenceId: insuranceReference.referenceId,
        company: 'Insurance Verification',
        email: insuranceReference.contactEmail,
        fullName: `${insuranceReference.companyName}`,
        parenthesesText: `${insuranceReference.contactName}${insuranceReference.contactEmail ? ` - ${insuranceReference.contactEmail}` : ``}`,
        isInsuranceReference: true,
      };
      assigneeListToSet.unshift(insuranceReferenceAsassignee);
    }
    setAssigneeList(_.compact(assigneeListToSet));
  }, [relatedUsers, lenderProfiles, vo, lpContactsData, effectiveCreditApp, creditSubmissionsData, defaultRegarding]);

  useEffect(() => {
    if (taskId !== null && !_.isEmpty(tasksData)) {
      const currentTask = tasksData.find(t => t.taskId === taskId);

      const {
        assignedByAccount,
        assignedByUser,
        assignedToAccount,
        status,
        regarding,
        priority,
        assignedToUserProfileId,
        assignedByUserProfileId,
        notifyByEmail,
        notifyByText,
        taskItems,
        vendorOpportunityId,
        dueDateTime,
        notificationContactIds,
        notifyAssignorOnReminder,
        ownerPgId,
        completedDateTime,
      } = currentTask;
      setTask(currentTask);

      //Load task info into Form
      setAssignedByAccount(assignedByAccount);
      setAssignedByUser(assignedByUser);
      setIsSystemTask(assignedToAccount === 'System');
      setAssignedByUserProfileId(assignedByUserProfileId);
      setAssignedToUserProfileId(assignedToUserProfileId);
      setAssignedToOwnerPgId(ownerPgId);
      setStatus(status);
      setPriority(priority);
      setNotifyByEmail(notifyByEmail);
      setNotifyByText(notifyByText);
      setRegarding(regarding);
      setVendorOpportunityId(vendorOpportunityId);
      setCompletedDateTime(completedDateTime);
      setTaskDueDateTime(dueDateTime);
      setTaskNotificationContactIds(notificationContactIds?.length > 0 ? [...notificationContactIds] : []);
      setNotifyAssignorOnReminder(notifyAssignorOnReminder);

      setTaskItems(
        (taskItems ?? []).map(taskItem => ({
          ...taskItem,
          invoiceQuoteNumber: null,
          doc: taskItem.doc ? { ...taskItem.doc, file: null, reader: null } : { file: null, reader: null },
        }))
      );

      setTaskAssignedToMe(assignedToUserProfileId === userProfile.id);
    }
  }, [taskId, tasksData, userProfile.id]);

  useEffect(() => {
    if (!_.isEmpty(assigneeList)) {
      let assignee;
      let assignor;
      if (!taskId) {
        assignor = _.find(assigneeList, { id: userProfile.id });
        setAssignedByUser(userProfile.fullName);
        setAssignedByUserProfileId(userProfile.id);
        setAssignedByAccount(account.name);
        setAssignee(null);
        if (_.get(assignor, 'mugshot', false)) {
          setAvatar(`https://${config.S3_BUCKET_NAME}.s3-us-west-2.amazonaws.com/${assignor.mugshot}`);
        }
      } else {
        assignee =
          _.find(assigneeList, { id: assignedToUserProfileId }) ||
          _.find(assigneeList, { ownerPgId: assignedToOwnerPgId }) ||
          _.find(assigneeList, { referenceId: task?.reference?.referenceId });
        assignor = _.find(assigneeList, { id: assignedByUserProfileId });
        setAssignee(assignee);

        if (_.get(assignee, 'mobilePhoneNumber', false)) {
          setEnabledForSMS(true);
        } else {
          setEnabledForSMS(false);
        }
        if (_.get(assignor, 'mugshot', false)) {
          setAvatar(`https://${config.S3_BUCKET_NAME}.s3-us-west-2.amazonaws.com/${assignor.mugshot}`);
        }
      }
    }
  }, [assignedByUserProfileId, assignedToUserProfileId, assignedToOwnerPgId, assigneeList, userProfile, account]);

  const handleDownload = doc => {
    try {
      const link = document.createElement('a');
      link.href = doc.source;
      link.setAttribute('target', '_blank');
      link.setAttribute('download', doc.docName);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      console.log(error);
    }
  };

  const handleStatus = event => setStatus(event.target.value);
  const handlePriority = event => setPriority(event.target.value);
  const handleCloseUploadZone = () => setOpenUploadZone(false);

  const handleAddDoc = index => {
    setTaskItemIndex(index);
    setOpenUploadZone(true);
  };
  const handleAddItems = () => {
    setTaskItems([
      ...taskItems,
      {
        type: taskItemTypes.docs,
        taskAssigneeNotes: '',
        taskAssignorNotes: '',
        docType: assigneeIsApplicant ? 'driver license' : 'invoice',
        doc: {
          file: null,
          reader: null,
        },
      },
    ]);
  };

  const handleRemoveItems = () => {
    setTaskItems(prev => prev.filter((_, index) => index !== prev.length - 1));
  };

  const handleChangeNotificationReps = val => {
    setTaskNotificationContactIds(val.map(obj => obj.id));
  };

  const handleSaveTaskItems = (index, key, value) => {
    setTaskItems(prev => prev.map((taskItem, i) => (i === index ? { ...taskItem, [key]: value } : taskItem)));
  };

  const handleSaveTaskItemFile = file => {
    const applyFile = reader => {
      setTaskItems(prev =>
        prev.map((taskItem, i) =>
          i === taskItemIndex ? { ...taskItem, doc: { ...taskItem.doc, file, reader } } : taskItem
        )
      );
    };

    if (!file.type.includes('image')) {
      applyFile(documentIcon);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => applyFile(reader.result);
  };

  const handleChangeTaskItemType = (index, nextType) => {
    const previousType = _.get(taskItems, [index, 'type']);
    handleSaveTaskItems(index, 'type', nextType);

    const nextSpecialTypeConfig = getSpecialTaskItemTypeConfig(nextType);
    if (nextSpecialTypeConfig) {
      handleSaveTaskItems(index, 'taskAssignorNotes', nextSpecialTypeConfig.assignorNote);
      setRegarding(nextSpecialTypeConfig.regarding);
      return;
    }

    if (getSpecialTaskItemTypeConfig(previousType)) {
      handleSaveTaskItems(index, 'taskAssignorNotes', '');
      setRegarding(defaultRegarding);
    }
  };

  const uploadTaskItemDocuments = async () => {
    const uploadedDocumentIdByTaskItemId = {};

    await asyncForEach(taskItems, async taskItem => {
      if (taskItem.type !== taskItemTypes.docs || !taskItem.doc?.file) return;

      try {
        const response = await fileInput({
          variables: {
            input: collectUploadDocumentInput(taskItem, {
              vendorOpportunityId,
              creditAppId: effectiveCreditApp?.id,
              createdBy: userProfile.fullName,
            }),
            file: taskItem.doc.file,
          },
        });

        const documentId = _.get(response, 'data.upsertDocument.document');
        if (documentId) {
          uploadedDocumentIdByTaskItemId[taskItem.taskItemId] = documentId;
        }
      } catch (err) {
        logError(err);
      }
    });

    return uploadedDocumentIdByTaskItemId;
  };

  const formValues = {
    regarding,
    status,
    priority,
    dueDateTime: taskDueDateTime,
    notifyByEmail,
    notifyByText,
    notifyAssignorOnReminder,
    notificationContactIds: taskNotificationContactIds,
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      if (taskId) {
        const uploadedDocumentIdByTaskItemId = await uploadTaskItemDocuments();

        await handleSaveTask({
          taskId,
          input: collectUpdateTaskInput({
            taskId,
            account,
            assignee,
            taskItems,
            uploadedDocumentIdByTaskItemId,
            values: formValues,
            completedDateTime: resolveCompletedDateTime(status, completedDateTime),
          }),
        });
      } else {
        await handleAddTask(
          collectCreateTaskInput({
            account,
            assignee,
            assigneeKind,
            creditSubId,
            creditSubmission,
            specialTaskItemType,
            taskItems,
            userProfile,
            vendorOpportunityId,
            values: formValues,
            creditApplication: effectiveCreditApp,
          })
        );
      }
    } catch (err) {
      logError(err);
    }

    if (refetchDocs) {
      await refetchDocs();
    }
    setLoading(false);
    handleClose();
  };

  const getDocumentTypeOptions = () => {
    let result;
    switch (true) {
      case assigneeIsApplicant:
        result = documentTypeOptionsForApplicants;
        break;
      case taskAssignedToMe:
        result = documentTypeOptionsForLenderAssignment;
        break;
      default:
        result = documentTypeOptionsForDealerAssignment;
    }
    // reject for current user role
    result = rejectHiddenDocumentTypeOptions(documentsPortalConfiguration, result, userProfile.vendorContactRole);
    if (assignee) {
      // reject for assignee user role
      result = rejectHiddenDocumentTypeOptions(documentsPortalConfiguration, result, assignee.vendorContactRole);
    }
    return result;
  };

  const documentTypeOptions = getDocumentTypeOptions();

  const isFallbackHydrationLoading =
    !!taskId &&
    ((!shouldSkipVendorOpportunityQuery && taskVendorOpportunityLoading) ||
      (!shouldSkipCreditAppByVoQuery && creditAppByVoLoading));

  const formTitle = taskId ? 'Edit Task' : 'Add Task';

  if (isFallbackHydrationLoading || loading) {
    return (
      <div style={modalStyle} className={classes.surface}>
        <Tile title={formTitle} maxHeightLimitByScreen titleIcon={<AssignmentInd />}>
          <div className={classes.formContainer}>
            <LinearProgress />
          </div>
        </Tile>
      </div>
    );
  }

  return (
    <div style={modalStyle} className={classes.surface}>
      <IconButton aria-label="close" className={classes.closeButton} onClick={handleClose} size="large">
        <CloseIcon />
      </IconButton>

      <Tile title={formTitle} maxHeightLimitByScreen titleIcon={<AssignmentInd />}>
        <div className={classes.formContainer}>
          {/* Form start */}
          <form noValidate autoComplete="off">
            <Grid container spacing={4} className={classes.formContainerGridContainer}>
              <Grid item xs={12} sm={12} md={12} lg={12} className={classes.subHeaderContainer}>
                <Typography className={classes.taskTitle}>Task Details</Typography>

                <Avatar alt="profilePicture" src={avatar} />
              </Grid>

              <Grid item xs={12} sm={12} md={10} lg={10}>
                {isSystemTask ? (
                  <Typography className={classes.taskAssigned}>Assigned by System: Trnsact</Typography>
                ) : (
                  <Typography className={classes.taskAssigned}>
                    Assigned by: {assignedByUser} {assignedByAccount ? `(${assignedByAccount})` : ''}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={6}>
                <TextField
                  fullWidth
                  id="regarding"
                  label="Regarding"
                  variant="outlined"
                  formControlProps={{ fullWidth: true }}
                  disabled={assigneeIsTradeReference || assigneeIsInsuranceReference || !!specialTaskItemType}
                  slotProps={{
                    htmlInput: {
                      type: 'text',
                      onChange: e => {
                        setRegarding(e.target.value);
                      },
                      value: regarding,
                    },
                    inputLabel: { shrink: true },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} lg={6}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label="DUE DATE:"
                    sx={{ width: '100% ' }}
                    slotProps={{ textField: { variant: 'outlined' } }}
                    value={taskDueDateTime ? moment(taskDueDateTime) : null}
                    onChange={val => setTaskDueDateTime(val.toISOString())}
                  />
                </LocalizationProvider>
              </Grid>

              {assigneeIsTradeReference && status === taskStatuses.complete && <TradeReferenceResults task={task} />}

              {!isSystemTask && (
                <>
                  <Grid item xs={12} sm={12} md={6} lg={6}>
                    <FormControl variant="outlined" fullWidth style={{ margin: '8px 0' }}>
                      <InputLabel id="status-select-label">Status</InputLabel>
                      <Select
                        value={status}
                        label="Status"
                        id="status-select"
                        variant="outlined"
                        labelId="status-select-label"
                        onChange={handleStatus}
                      >
                        {statusSelectOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={12} md={6} lg={6}>
                    <FormControl fullWidth style={{ margin: '8px 0' }}>
                      <InputLabel id="priority-select-label">Priority</InputLabel>
                      <Select
                        value={priority}
                        label="Priority"
                        onChange={handlePriority}
                        labelId="priority-select-label"
                        inputProps={{ name: 'priority', id: 'priority' }}
                        variant="outlined"
                      >
                        {prioritySelectOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={12} md={6} lg={6}>
                    <Grid container>
                      <Grid item xs={12} sm={12} md={12} lg={12}>
                        <Autocomplete
                          value={assignee}
                          options={assigneeList}
                          groupBy={option => option.company}
                          disabled={task?.taskId}
                          onChange={(event, contact) => {
                            const nextKind = getAssigneeKind(contact);

                            if (isReferenceAssigneeKind(nextKind) || isReferenceAssigneeKind(assigneeKind)) {
                              setRegarding(getRegardingForAssigneeKind(nextKind, defaultRegarding));
                            }

                            setEnabledForSMS(!!contact?.mobilePhoneNumber);
                            setAssignee(contact);
                          }}
                          getOptionLabel={option =>
                            `${option?.fullName ? option?.fullName : ``}${
                              option.parenthesesText ? ` (${option.parenthesesText})` : ''
                            }`
                          }
                          style={{ height: 40 }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label="Assigned To"
                              variant="outlined"
                              formControlProps={{
                                fullWidth: true,
                              }}
                              placeholder="Search"
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {assignee && (assigneeIsTradeReference || assigneeIsInsuranceReference) && (
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <TextField
                        label="Email"
                        variant="outlined"
                        fullWidth
                        onChange={e => {
                          setAssignee(prev => ({ ...prev, email: e.target.value }));
                        }}
                        value={assignee.email ?? ''}
                        error={isAssigneeEmailInvalid}
                        helperText={isAssigneeEmailInvalid && 'Please enter a valid email'}
                        disabled={task?.taskId}
                      />
                    </Grid>
                  )}

                  {assignee && assigneeIsInsuranceReference && (
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <FormControl variant="outlined" fullWidth style={{ margin: '8px 0' }}>
                        <InputLabel id={`credit-submission-label`}>Credit Submission</InputLabel>
                        <Select
                          labelId={`credit-submission-label`}
                          value={creditSubmission}
                          variant="outlined"
                          onChange={e => {
                            setCreditSubmission(e.target.value);
                          }}
                        >
                          {creditSubmissionsData?.creditSubmissionsByDynamicsVOId?.map(cs => (
                            <MenuItem key={cs.creditSubmissionId} value={cs}>
                              {`${cs.lenderName}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}

                  {assignee && !assigneeIsTradeReference && !assigneeIsInsuranceReference && (
                    <Grid item xs={12} sm={12} md={6} lg={6}>
                      <h5 className={classes.titleChatText}>Notify Via</h5>
                      <Grid className={classes.notifyCheckboxesContainer}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="medium"
                              name="notifyViaEmail"
                              className={classes.chatCheckboxStyles}
                              checked={notifyByEmail}
                              onChange={() => setNotifyByEmail(!notifyByEmail)}
                            />
                          }
                          label="Email"
                          size="small"
                        />
                        {enabledForSMS && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="medium"
                                disabled={!enabledForSMS}
                                name="notifyViaSMS"
                                className={classes.chatCheckboxStyles}
                                checked={notifyByText}
                                onChange={() => setNotifyByText(!notifyByText)}
                              />
                            }
                            size="small"
                            label="SMS"
                          />
                        )}
                      </Grid>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={12} md={12} lg={12} style={{ margin: '24px 0' }}>
                    <Grid container>
                      <Grid item xs={12} sm={12} md={12} lg={12}>
                        <br />
                        <h4 style={{ display: 'flex', alignItems: 'center' }}>
                          <People color="action" size="small" style={{ marginRight: 4 }} />
                          Additional Notification Contacts
                        </h4>
                        <FormControl variant="outlined" fullWidth>
                          <Autocomplete
                            multiple
                            value={assigneeList.filter(val => taskNotificationContactIds.includes(val.id))}
                            options={assigneeList.filter(val => !taskNotificationContactIds.includes(val.id))}
                            onChange={(event, newValue) => {
                              handleChangeNotificationReps(newValue);
                            }}
                            isOptionEqualToValue={(option, value) => {
                              return option.id === value;
                            }}
                            getOptionLabel={option => {
                              return option.fullName;
                            }}
                            renderInput={params => (
                              <TextField
                                {...params}
                                label="Contacts"
                                variant="outlined"
                                formControlProps={{
                                  fullWidth: true,
                                }}
                                placeholder=""
                              />
                            )}
                          />
                        </FormControl>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={notifyAssignorOnReminder}
                              onChange={() => setNotifyAssignorOnReminder(!notifyAssignorOnReminder)}
                              name="checkedB"
                              color="primary"
                            />
                          }
                          label="Notify Assignor & Additional Contacts on reminder"
                          style={{ color: '#000000', marginTop: 16 }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {assignee && !assigneeIsTradeReference && !assigneeIsInsuranceReference && (
                    <Stack gap="1rem" paddingLeft="2rem" width="100%">
                      <Stack direction="row" gap="0.5rem">
                        <ListIcon color="action" size="small" />
                        <Typography>Task Items</Typography>
                      </Stack>

                      {!taskItems.length && (
                        <Alert severity="info" sx={{ width: '100%' }}>
                          Click on 'Add' to starting adding Task Items
                        </Alert>
                      )}

                      <Stack gap="2rem">
                        {taskItems.map((taskItem, index) => (
                          <div key={`taskItem.${index}.general`}>
                            <Grid container spacing={2}>
                              <DropzoneDialog
                                id={`dropZone.${index}`}
                                key={`dropZone[${index}]`}
                                open={openUploadZone}
                                onDrop={() => {}}
                                onSave={file => {
                                  handleSaveTaskItemFile(file[0]);
                                  handleCloseUploadZone();
                                }}
                                onClose={() => {
                                  handleCloseUploadZone();
                                }}
                                filesLimit={1}
                                dropzoneText={'Drag or Click Here To Upload File'}
                                showPreviews={false}
                                maxFileSize={MAX_UPLOAD_FILE_SIZE}
                              />

                              <Grid item xs={12} sm={12} md={12} lg={12}>
                                <Typography margin="0.5rem 0">Task Item #{index + 1}</Typography>
                              </Grid>

                              <Grid item xs={12} sm={6} md={6} lg={6}>
                                <FormControl variant="outlined" fullWidth style={{ margin: '8px 0' }}>
                                  <InputLabel id="type-select-label">Type</InputLabel>
                                  <Select
                                    label="Type"
                                    labelId="type-select-label"
                                    key={`taskItems[${index}].type`}
                                    value={taskItem.type}
                                    variant="outlined"
                                    onChange={e => {
                                      handleChangeTaskItemType(index, e.target.value);
                                    }}
                                    inputProps={{
                                      name: `taskItems[${index}].type`,
                                      id: `taskItems[${index}].type`,
                                      disabled: taskAssignedToMe && taskId,
                                    }}
                                  >
                                    <MenuItem value={taskItemTypes.docs}>Document Upload</MenuItem>
                                    {!assigneeIsApplicant && (
                                      <MenuItem value={taskItemTypes.info}>Information Request</MenuItem>
                                    )}
                                    {isDocumentESignTaskAvailable && assigneeIsApplicant && (
                                      <MenuItem value={taskItemTypes.documentESign}>Document E-Sign</MenuItem>
                                    )}
                                    {assigneeIsApplicant &&
                                      taskItems.length === 1 &&
                                      availableSpecialTaskItemTypes.map(type => (
                                        <MenuItem key={type} value={type}>
                                          {getSpecialTaskItemTypeConfig(type).label}
                                        </MenuItem>
                                      ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              {taskItem.type === taskItemTypes.docs ? (
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                  <FormControl variant="outlined" fullWidth style={{ margin: '8px 0' }}>
                                    <InputLabel id={`docType-select-label`}>Document Type</InputLabel>
                                    <Select
                                      label="Document Type"
                                      labelId={`docType-select-label`}
                                      value={taskItem.docType || ''}
                                      key={`taskItem[${index}].docType`}
                                      variant="outlined"
                                      onChange={e => {
                                        handleSaveTaskItems(index, 'docType', e.target.value);
                                      }}
                                      inputProps={{
                                        name: `taskItems${index}].docType`,
                                        id: `taskItems${index}].docType`,
                                        disabled: taskAssignedToMe,
                                      }}
                                    >
                                      {documentTypeOptions.map((item, key) => {
                                        return (
                                          <MenuItem
                                            key={`taskItems${index}].docType[${item.value}]`}
                                            value={item.value}
                                          >
                                            {item.text}
                                          </MenuItem>
                                        );
                                      })}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              ) : null}

                              {taskItem.type === taskItemTypes.documentESign && (
                                <Grid item xs={12} sm={6} md={6} lg={6}>
                                  <FormControl variant="outlined" fullWidth style={{ margin: '8px 0' }}>
                                    <InputLabel id={`document-select-label`}>Document</InputLabel>
                                    <Select
                                      label="Document"
                                      labelId={`document-select-label`}
                                      value={taskItem.documentId || ''}
                                      key={`taskItem[${index}].document`}
                                      variant="outlined"
                                      onChange={e => {
                                        const document = documentsForESign?.find(d => d.documentId === e.target.value);
                                        handleSaveTaskItems(index, 'documentId', document.documentId);
                                        handleSaveTaskItems(index, 'docType', document.docType);
                                      }}
                                      inputProps={{
                                        name: `taskItems${index}].document`,
                                        id: `taskItems${index}].document`,
                                      }}
                                    >
                                      {documentsForESign?.map(document => (
                                        <MenuItem
                                          key={`taskItems${index}].${document.documentId}`}
                                          value={document.documentId}
                                        >
                                          {`${document.docName} - ${document.docType}`}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Grid>
                              )}

                              <Grid item xs={12} sm={12} md={12} lg={12}>
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  label={taskAssignedToMe ? 'Notes from Assignor' : 'Notes to Assignee'}
                                  id={`taskItem.${index}.taskAssignorNotes`}
                                  slotProps={{
                                    htmlInput: {
                                      type: 'text',
                                      disabled: taskAssignedToMe,
                                      onChange: e => {
                                        handleSaveTaskItems(index, 'taskAssignorNotes', e.target.value);
                                      },
                                      value: taskItem.taskAssignorNotes || '',
                                    },
                                    inputLabel: {
                                      shrink: true,
                                    },
                                  }}
                                />
                              </Grid>
                              {taskAssignedToMe && (
                                <Grid item xs={12} sm={12} md={12} lg={12}>
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    label="Response to Assignor"
                                    id={`taskItem.${index}.taskAssigneeNotes`}
                                    slotProps={{
                                      htmlInput: {
                                        type: 'text',
                                        disabled: !taskAssignedToMe,
                                        onChange: e => {
                                          handleSaveTaskItems(index, 'taskAssigneeNotes', e.target.value);
                                        },
                                        value: taskItem.taskAssigneeNotes || '',
                                      },
                                      inputLabel: {
                                        shrink: true,
                                      },
                                    }}
                                  />
                                </Grid>
                              )}

                              {taskItem.type === taskItemTypes.docs && (
                                <>
                                  {/* SHOWN IN EDIT MODE */}
                                  {_.get(taskItem, 'doc.documentId', false) ? (
                                    <>
                                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                        {_.get(taskItem.doc, 'docName', false) ? (
                                          <TextField
                                            className={classes.textField}
                                            variant="outlined"
                                            label="File Name"
                                            key={`docs[${index}].file`}
                                            formControlProps={{ fullWidth: true }}
                                            slotProps={{
                                              htmlInput: {
                                                type: 'text',
                                                id: `docs[${index}].file`,
                                                value: taskItem.doc.docName || '',
                                              },

                                              inputLabel: {
                                                shrink: true,
                                              },
                                            }}
                                          />
                                        ) : null}
                                      </Grid>
                                      <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                                        {_.get(taskItem.doc, 'createdBy', false) ? (
                                          <TextField
                                            className={classes.textField}
                                            variant="outlined"
                                            label="Uploaded by"
                                            key={`docs[${index}].createdBy`}
                                            formControlProps={{ fullWidth: true }}
                                            slotProps={{
                                              htmlInput: {
                                                type: 'text',
                                                id: `docs[${index}].createdBy`,
                                                value: taskItem.doc.createdBy || '',
                                              },

                                              inputLabel: {
                                                shrink: true,
                                              },
                                            }}
                                          />
                                        ) : null}
                                      </Grid>
                                      <Grid item xs={12} sm={12} md={2} lg={2} xl={2}>
                                        {_.get(taskItem.doc, 'source', false) ? (
                                          <Tooltip
                                            id="tooltip-top-start"
                                            title="Click to Download Document"
                                            placement="top"
                                          >
                                            <Fab
                                              color="primary"
                                              size="small"
                                              onClick={() => handleDownload(taskItem.doc)}
                                              aria-label="download"
                                            >
                                              <GetAppIcon />
                                            </Fab>
                                          </Tooltip>
                                        ) : null}
                                      </Grid>
                                    </>
                                  ) : (
                                    <>
                                      {/* SHOWN IN EDIT MODE */}
                                      {taskAssignedToMe ? (
                                        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                                          <Button
                                            onClick={() => {
                                              handleAddDoc(index);
                                            }}
                                            color="dribbble"
                                            size={'sm'}
                                          >
                                            <CloudUploadIcon size="sm" />
                                            Upload Document
                                          </Button>
                                        </Grid>
                                      ) : null}
                                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                        {_.get(taskItem.doc, 'file.name', false) ? (
                                          <TextField
                                            className={classes.textField}
                                            variant="outlined"
                                            label="File Name"
                                            key={`taskItem.doc[${index}].file`}
                                            formControlProps={{ fullWidth: true }}
                                            slotProps={{
                                              htmlInput: {
                                                type: 'text',
                                                id: `taskItem.doc[${index}].file`,
                                                value: taskItem.doc.file.name || '',
                                              },

                                              inputLabel: {
                                                shrink: true,
                                              },
                                            }}
                                          />
                                        ) : null}
                                      </Grid>

                                      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                                        {_.get(taskItem.doc, 'reader', false) ? (
                                          <img src={taskItem.doc.reader} className={classes.previewDoc} />
                                        ) : null}
                                      </Grid>
                                    </>
                                  )}
                                </>
                              )}
                            </Grid>
                          </div>
                        ))}
                      </Stack>

                      {!taskAssignedToMe && (
                        <Stack direction="row" gap="0.5rem">
                          {!specialTaskItemType && (
                            <Button
                              disabled={_.isEmpty(assignee)}
                              onClick={handleAddItems}
                              color="primary"
                              startIcon={<AddCircleIcon />}
                            >
                              Add
                            </Button>
                          )}

                          {!!taskItems.length && (
                            <Button onClick={handleRemoveItems} startIcon={<RemoveCircleIcon />} color="error">
                              Remove
                            </Button>
                          )}
                        </Stack>
                      )}
                    </Stack>
                  )}
                </>
              )}
            </Grid>
          </form>

          {!isSystemTask && (
            <Stack direction="row" justifyContent="flex-end" sx={{ marginTop: '40px' }}>
              <Button
                color="success"
                variant="contained"
                onClick={handleSubmit}
                disabled={!isSubmitEnabled}
                startIcon={<PublishIcon />}
              >
                Save
              </Button>
            </Stack>
          )}
        </div>
      </Tile>
    </div>
  );
}
